import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, TRY, formatApiErrorDetail, sendResendEmail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ArrowUpRight, ArrowDownRight, Building2 } from "lucide-react";

const empty = { type: "expense", amount: "", facility: "", category: "", note: "", date: new Date().toISOString().slice(0, 10) };

function inRange(dateStr, range) {
  const d = new Date(dateStr);
  const now = new Date();
  if (range === "weekly") return (now - d) / 86400000 <= 7;
  if (range === "monthly") return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  if (range === "yearly") return d.getFullYear() === now.getFullYear();
  return true;
}

export default function Finance() {
  const [items, setItems] = useState([]);
  const [range, setRange] = useState("monthly");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/transactions").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(empty);
    setEditId(null);
    setOpen(true);
  };
  const openEdit = (it) => {
    setForm({ ...it, amount: String(it.amount) });
    setEditId(it.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.amount || !form.date) return toast.error("Tutar ve tarih zorunlu");
    const payload = { ...form, amount: parseFloat(form.amount) };
    try {
      if (editId) await api.put(`/transactions/${editId}`, payload);
      else await api.post("/transactions", payload);
      toast.success(editId ? "İşlem güncellendi" : "İşlem eklendi");
      setOpen(false);
      load();

      // Otomatik E-posta Bildirimi: Gelir/Gider İşlem Alıcılarına Mail Gönder
      try {
        const config = JSON.parse(localStorage.getItem("1011_email_automation_config") || "{}");
        const recipients = config.financeEmails || "";
        if (recipients.trim()) {
          const typeLabel = payload.type === "income" ? "💰 Gelir" : "💸 Gider";
          await sendResendEmail({
            to: recipients,
            subject: `${typeLabel} İşlemi: ${TRY(payload.amount)} - ${payload.category || "Genel"}`,
            html: `
              <div style="font-family:Arial,sans-serif;padding:24px;background:#0f172a;color:#f8fafc;border-radius:12px;">
                <h2 style="color:#38bdf8;margin-top:0;">📊 10-11 Gelir/Gider Bildirimi</h2>
                <table style="width:100%;color:#cbd5e1;font-size:14px;">
                  <tr><td style="padding:8px 0;"><strong>Tür:</strong></td><td>${typeLabel}</td></tr>
                  <tr><td style="padding:8px 0;"><strong>Tutar:</strong></td><td style="font-size:18px;font-weight:bold;color:#38bdf8;">${TRY(payload.amount)}</td></tr>
                  <tr><td style="padding:8px 0;"><strong>Saha:</strong></td><td>${payload.facility || "-"}</td></tr>
                  <tr><td style="padding:8px 0;"><strong>Kategori:</strong></td><td>${payload.category || "-"}</td></tr>
                  <tr><td style="padding:8px 0;"><strong>Tarih:</strong></td><td>${payload.date}</td></tr>
                  <tr><td style="padding:8px 0;"><strong>Not:</strong></td><td>${payload.note || "-"}</td></tr>
                </table>
                <hr style="border:0;border-top:1px solid #334155;margin:16px 0;" />
                <p style="font-size:11px;color:#64748b;">10-11 Yönetim Platformu Otomatik Bildirim</p>
              </div>
            `
          });
        }
      } catch (emailErr) {
        console.warn("Finance email notification failed:", emailErr);
      }

    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/transactions/${id}`);
    toast.success("Silindi");
    load();
  };

  const filtered = items.filter((t) => inRange(t.date, range));
  const income = filtered.filter((t) => t.type === "income").reduce((s, t) => s + Number(t.amount), 0);
  const expense = filtered.filter((t) => t.type === "expense").reduce((s, t) => s + Number(t.amount), 0);

  const byFacility = Object.values(
    filtered.reduce((acc, t) => {
      const key = t.facility?.trim() || "Diğer / Belirtilmemiş";
      if (!acc[key]) acc[key] = { facility: key, income: 0, expense: 0 };
      if (t.type === "income") acc[key].income += Number(t.amount);
      else acc[key].expense += Number(t.amount);
      return acc;
    }, {})
  ).sort((a, b) => b.expense - a.expense);

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-primary mb-2">Finans</p>
          <h1 className="font-anton text-4xl sm:text-5xl">GELİR / GİDER</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-transaction-button" onClick={openNew} className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="w-4 h-4 mr-1" /> Kayıt Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-anton text-2xl">{editId ? "KAYDI DÜZENLE" : "YENİ KAYIT"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Tür</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="transaction-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">Gelir</SelectItem>
                      <SelectItem value="expense">Gider</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tutar (₺)</Label>
                  <Input type="number" data-testid="transaction-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Hangi Saha</Label>
                <Input data-testid="transaction-facility-input" value={form.facility} onChange={(e) => setForm({ ...form, facility: e.target.value })} placeholder="Tesis / saha adı" />
              </div>
              <div className="space-y-2">
                <Label>Kategori / Malzeme</Label>
                <Input data-testid="transaction-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Örn: Top, kira, komisyon..." />
              </div>
              <div className="space-y-2">
                <Label>Tarih</Label>
                <Input type="date" data-testid="transaction-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Not</Label>
                <Input data-testid="transaction-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="İsteğe bağlı" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-transaction-button" onClick={save} className="bg-primary hover:bg-primary/90 text-white">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
        <Tabs value={range} onValueChange={setRange}>
          <TabsList className="bg-secondary">
            <TabsTrigger value="weekly">Haftalık</TabsTrigger>
            <TabsTrigger value="monthly">Aylık</TabsTrigger>
            <TabsTrigger value="yearly">Yıllık</TabsTrigger>
            <TabsTrigger value="all">Tümü</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex gap-3 text-sm">
          <span className="text-primary font-semibold">Gelir: {TRY(income)}</span>
          <span className="text-destructive font-semibold">Gider: {TRY(expense)}</span>
          <span className="font-semibold">Net: {TRY(income - expense)}</span>
        </div>
      </div>

      <Card className="bg-card border-border overflow-hidden" data-testid="facility-report">
        <div className="p-5 border-b border-border flex items-center gap-2">
          <Building2 className="w-5 h-5 text-primary" />
          <h2 className="font-anton text-2xl">SAHA RAPORU</h2>
          <span className="text-sm text-muted-foreground ml-auto">Saha bazlı gelir & gider dökümü</span>
        </div>
        {byFacility.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Bu dönemde kayıt yok.</div>
        ) : (
          <div className="divide-y divide-border">
            {byFacility.map((r) => {
              const max = Math.max(...byFacility.map((x) => x.expense), 1);
              return (
                <div key={r.facility} className="p-4 sm:px-5">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <span className="font-semibold truncate">{r.facility}</span>
                    <div className="flex gap-4 text-sm shrink-0">
                      {r.income > 0 && <span className="text-primary">+{TRY(r.income)}</span>}
                      <span className="text-destructive font-semibold">Ödenen: {TRY(r.expense)}</span>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-destructive/70 rounded-full" style={{ width: `${(r.expense / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
            <div className="p-4 sm:px-5 flex items-center justify-between bg-secondary/40">
              <span className="font-anton text-lg">TOPLAM ÖDENEN</span>
              <span className="font-anton text-lg text-destructive">{TRY(expense)}</span>
            </div>
          </div>
        )}
      </Card>

      <div className="space-y-2" data-testid="transactions-list">
        {filtered.length === 0 && (
          <Card className="bg-card border-border p-10 text-center text-muted-foreground">
            Bu dönemde kayıt yok.
          </Card>
        )}
        {filtered.map((t, i) => (
          <motion.div key={t.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }}>
            <Card className="bg-card border-border p-4 flex items-center gap-4 hover:border-primary/40 transition-colors">
              <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${t.type === "income" ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`}>
                {t.type === "income" ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold truncate">{t.category || (t.type === "income" ? "Gelir" : "Gider")}</span>
                  {t.facility && <Badge variant="outline" className="border-border text-xs">{t.facility}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{t.date}{t.note ? ` · ${t.note}` : ""}</p>
              </div>
              <span className={`font-anton text-xl ${t.type === "income" ? "text-primary" : "text-destructive"}`}>
                {t.type === "income" ? "+" : "-"}{TRY(t.amount)}
              </span>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(t)} className="h-8 w-8"><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" data-testid={`delete-transaction-${t.id}`} onClick={() => remove(t.id)} className="h-8 w-8 text-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
